import { useRef, useState } from "react";

import { IconPencil, IconUpload, IconUser } from "@tabler/icons";
import { useTheme } from "styled-components";

import { useDeleteProfilePictureMutation, useUploadProfilePictureMutation } from "@sso/apis/account";
import { useAppSelector } from "@sso/hooks/useRedux";
import { selectCurrentUser } from "@sso/store/slices/auth";
import { Button } from "@sso/ui/Button";
import { CircleLoading } from "@sso/ui/CircleLoading";
import { Flex, GridItem } from "@sso/ui/FlexBox";

const boxSize = "200px";

export const ProfilePicture = () => {
  const theme = useTheme();
  const user = useAppSelector(selectCurrentUser);
  const inputRef = useRef<HTMLInputElement>(null!);
  const [picture, setPicture] = useState<File | null>(null);

  const [uploadPicture, { isLoading: uploadLoading }] = useUploadProfilePictureMutation();
  const [deletePicture, { isLoading: deleteLoading }] = useDeleteProfilePictureMutation();

  const picturePreview = picture ? URL.createObjectURL(picture as unknown as Blob) : "";
  const userHasPicture = !!user?.picture;

  const handleUoloadFileOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length <= 0) {
      return;
    }
    setPicture(files[0]);
  };

  const handleDeletePicture = () => {
    if (picturePreview) {
      return setPicture(null);
    }
    if (!userHasPicture) {
      return;
    }
    deletePicture();
  };

  const handleUploadPicture = () => {
    if (!picture) {
      return;
    }
    uploadPicture(picture)
      .unwrap()
      .then(() => setPicture(null));
  };

  const handleInputOnClick = () => (inputRef.current.value = ""); //to be able to select same file again

  return (
    <>
      <Flex
        minHeight={boxSize}
        minWidth={boxSize}
        height={boxSize}
        width={boxSize}
        bgColor={theme.colors.grey[300]}
        borderRadius="10%"
        position="relative"
        bgPosition="center"
        bgSize="cover"
        overflow="hidden"
        bgImage={picturePreview || user?.picture}
      >
        {!picturePreview && !userHasPicture && <IconUser size="50%" />}
        <Flex
          shadow={2}
          borderRadius="50%"
          position="absolute"
          padding="0.35rem"
          bottom="0"
          right="0"
          bgColor={theme.colors.primary.light}
          as="label"
          cursor="pointer"
        >
          {picturePreview ? <IconPencil /> : <IconUpload />}
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            hidden
            onClick={handleInputOnClick}
            onChange={handleUoloadFileOnChange}
          />
        </Flex>
        {(uploadLoading || deleteLoading) && <CircleLoading bgColor="black" size={6} borderSize={4} blurBackground />}
      </Flex>
      <Flex margin="1rem 0" width={boxSize}>
        <GridItem xs={6}>
          <Button fullWidth disabled={!picture} variant="outlined" color="primary" onClick={handleUploadPicture}>
            Upload
          </Button>
        </GridItem>
        <GridItem xs={6}>
          <Button
            fullWidth
            disabled={!userHasPicture && !picture}
            variant="outlined"
            color="error"
            onClick={handleDeletePicture}
          >
            Delete
          </Button>
        </GridItem>
      </Flex>
    </>
  );
};
